import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const NAV = [
    { to: "/", label: "Accueil" },
    { to: "/ouverture-de-porte", label: "Ouverture de porte" },
    { to: "/services", label: "Services" },
    { to: "/zones-d-intervention", label: "Zones d'intervention" },
    { to: "/tarifs", label: "Tarifs" },
    { to: "/devis-contact", label: "Devis gratuit" },
];

const DEPANNAGE = [
    { to: "/sos-serrurier", label: "SOS Serrurier urgence" },
    { to: "/serrurier-paris", label: "Serrurier Paris" },
    { to: "/serrurier-autour-de-moi", label: "Serrurier autour de moi" },
    { to: "/bon-serrurier-paris", label: "Bon serrurier Paris" },
    { to: "/depannage-serrure-boite-aux-lettres", label: "Serrure boîte aux lettres" },
];

export default function Header() {
    const [open, setOpen] = useState(false);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 border-b border-midnight/5 bg-white/80 backdrop-blur-xl"
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" data-testid="header-logo" className="group flex items-center gap-3" onClick={() => setOpen(false)}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-midnight/10 bg-white shadow-[0_10px_28px_-10px_rgba(10,17,40,0.35)]">
                        <img src={`${process.env.PUBLIC_URL}/serrurier-logo.webp`} alt="Illustration serrurier Paris" className="h-10 w-10 object-contain" />
                    </span>
                    <span className="leading-none">
                        <span className="block font-display text-lg font-extrabold tracking-tight text-midnight">
                            Serrurier Paris
                        </span>
                        <span className="mt-1 block whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.02em] text-midnight/70">
                            Serrurier Sur Paris & Île-de-France
                        </span>
                    </span>
                </Link>

                <nav data-testid="desktop-nav" className="hidden items-center gap-0.5 xl:flex">
                    {NAV.slice(0, 3).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={({ isActive }) =>
                                `px-2.5 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    isActive ? "text-emergency" : "text-midnight/70 hover:text-midnight"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}

                    <div className="group relative" data-testid="nav-depannage-dropdown">
                        <button
                            type="button"
                            data-testid="nav-depannage-button"
                            className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold text-midnight/70 transition-colors duration-200 group-hover:text-midnight"
                        >
                            Dépannage
                            <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
                        </button>
                        <div className="invisible absolute left-0 top-full w-72 translate-y-2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                            <div className="overflow-hidden rounded-xl border border-midnight/10 bg-white py-2 shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]">
                                {DEPANNAGE.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        data-testid={`nav-depannage-${item.to.slice(1)}`}
                                        className={({ isActive }) =>
                                            `block px-5 py-3 text-sm font-semibold transition-colors ${
                                                isActive ? "bg-cloud text-emergency" : "text-midnight/70 hover:bg-cloud hover:text-midnight"
                                            }`
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    </div>

                    {NAV.slice(3).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={({ isActive }) =>
                                `px-2.5 py-2 text-sm font-semibold transition-colors duration-200 ${
                                    isActive ? "text-emergency" : "text-midnight/70 hover:text-midnight"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2.5">
                    <a
                        href={PHONE_TEL}
                        data-testid="header-call-button"
                        className="hidden items-center gap-2.5 bg-emergency px-4 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-emergency-dark md:flex"
                    >
                        <Phone className="h-4 w-4" />
                        <span className="leading-none">
                            <span className="block text-[10px] font-semibold uppercase tracking-wider opacity-80">Urgence 24/7</span>
                            <span className="mt-0.5 block whitespace-nowrap">{PHONE_DISPLAY}</span>
                        </span>
                    </a>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-11 w-11 items-center justify-center border border-midnight/10 text-midnight xl:hidden"
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-nav"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-midnight/5 bg-white xl:hidden"
                    >
                        <div className="space-y-1 px-4 py-4">
                            {NAV.slice(0, 3).map((item) => (
                                <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
                                    className={({ isActive }) => `block px-3 py-2.5 font-display text-lg font-extrabold ${isActive ? "text-emergency" : "text-midnight"}`}>
                                    {item.label}
                                </NavLink>
                            ))}
                            <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.25em] text-midnight/40">Dépannage</p>
                            {DEPANNAGE.map((item) => (
                                <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
                                    className={({ isActive }) => `block px-3 py-2 font-display text-base font-bold ${isActive ? "text-emergency" : "text-midnight/80"}`}>
                                    {item.label}
                                </NavLink>
                            ))}
                            {NAV.slice(3).map((item) => (
                                <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
                                    className={({ isActive }) => `block px-3 py-2.5 font-display text-lg font-extrabold ${isActive ? "text-emergency" : "text-midnight"}`}>
                                    {item.label}
                                </NavLink>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
